import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { MUSIC_VIDEO_TOOLS, useMusicVideoEditor, type MusicVideoTool } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TOOL_MESSAGES = {
  footage: editorMessages.toolFootage,
  text: editorMessages.toolText,
  lyrics: editorMessages.toolLyrics,
  look: editorMessages.toolLook,
} as const;

/** The line drawings of the four tools (design b1). */
export const TOOL_ICONS: Readonly<Record<MusicVideoTool, React.ReactNode>> = {
  footage: (
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="M16 10l5-3v10l-5-3" />
    </>
  ),
  text: (
    <>
      <path d="M5 6h14" />
      <path d="M12 6v13" />
      <path d="M9.5 19h5" />
    </>
  ),
  lyrics: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h10" />
      <path d="M4 17h13" />
    </>
  ),
  look: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v3" />
      <path d="M12 18v3" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
    </>
  ),
};

interface ToolRailProps {
  isPanelOpen: boolean;
  onPick: (tool: MusicVideoTool) => void;
}

/** The rail of the four tools (design b1): Footage, Text, Lyrics, Look. */
const ToolRailComponent: React.FC<ToolRailProps> = ({ isPanelOpen, onPick }) => {
  const intl = useIntl();
  const { tool } = useMusicVideoEditor();
  return (
    <nav className={styles.rail} aria-label={intl.formatMessage(editorMessages.toolsLabel)}>
      {MUSIC_VIDEO_TOOLS.map((item) => (
        <button
          key={item}
          type="button"
          className={clsx(styles.railTool, item === tool && isPanelOpen && styles.railToolOn)}
          aria-pressed={item === tool && isPanelOpen}
          onClick={() => onPick(item)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            {TOOL_ICONS[item]}
          </svg>
          {intl.formatMessage(TOOL_MESSAGES[item])}
        </button>
      ))}
    </nav>
  );
};

export const ToolRail = TypedMemo(ToolRailComponent);
